import React from "react";
import classNames from "classnames";
import {
	EORB3RecordState,
	TORB3BackendRecord,
	TORB3Record,
} from "redux/app/orb3/@types";

export type ORB3TableColumns = React.ReactNode[];

type OwnProps = {
	record: TORB3Record | TORB3BackendRecord;
	tableRows: ORB3TableColumns[];
	theme?: "light" | "dark";
};

export function renderDataTable(
	items: Record<string, string | number>[],
	columns: { key: string; label: string }[],
) {
	return (
		<table className="text-xs w-full border border-borderPrimary rounded">
			<thead>
				<tr className="bg-ws_table_header_background_secondary">
					{columns.map((c) => (
						<th key={c.key} className="p-1 text-left text-ws_table_header_font">
							{c.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{items.map((row, i) => (
					<tr key={i} className="border-t border-borderPrimary">
						{columns.map((c) => (
							<td key={c.key} className="p-1">
								{String(row[c.key] ?? "")}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	);
}

function ORB3TableRows(props: OwnProps) {
	const { record, tableRows, theme } = props;
	const userTheme = localStorage.getItem("theme") || "light";
	const resolvedTheme = theme ?? userTheme;
	const isStrikedOff = record.state === EORB3RecordState.STRIKED_OFF;

	return (
		<>
			{tableRows.map((tableRow, index) => {
				const isValidRow = tableRow.length === 4;
				if (!isValidRow) {
					return null;
				}
				const dateSection = tableRow[0];
				const codeSection = tableRow[1];
				const itemSection = tableRow[2];
				const contentSection = tableRow[3];
				const isContentEmpty =
					contentSection === "" ||
					contentSection === null ||
					contentSection === undefined;

				if (isContentEmpty) return null;

				return (
					<tr
						className={classNames("uppercase", {
							"line-through": isStrikedOff,
							"text-ws_input_flat_font": resolvedTheme === "dark",
						})}
						key={index}
					>
						<td className="relative">
							<div
								className={classNames(
									{
										"line-through": isStrikedOff,
										"text-ws_input_flat_font bg-ws_table_td_background":
											resolvedTheme === "dark",
										"bg-white": resolvedTheme === "light",
									},
									"p-3 m-px text-sm leading-7 text-left ws-marpol-td--divfix",
								)}
							>
								<div className="h-full">
									<span className="block font-medium whitespace-nowrap">
										{dateSection}
									</span>
								</div>
							</div>
						</td>
						<td className="relative">
							<div
								className={classNames(
									{
										"line-through": isStrikedOff,
										"text-ws_input_flat_font bg-ws_table_td_background":
											resolvedTheme === "dark",
										"text-gray-800 bg-white": resolvedTheme === "light",
									},
									"p-3 m-px text-sm font-medium leading-7 text-center ws-marpol-td--divfix",
								)}
							>
								{codeSection}
							</div>
						</td>
						<td className="relative">
							<div
								className={classNames(
									{
										"line-through": isStrikedOff,
										"text-ws_input_flat_font bg-ws_table_td_background":
											resolvedTheme === "dark",
										"text-gray-800 bg-white": resolvedTheme === "light",
									},
									"p-3 m-px text-sm font-medium leading-7 text-right ws-marpol-td--divfix",
								)}
							>
								<div className="h-full font-medium">{itemSection}</div>
							</div>
						</td>
						<td className="relative">
							<div
								className={classNames(
									{
										"text-ws_input_flat_font bg-ws_table_td_background":
											resolvedTheme === "dark",
										"text-gray-800 bg-white": resolvedTheme === "light",
									},
									"p-3 m-px text-sm font-medium leading-7 text-left",
								)}
							>
								<div className="font-medium uppercase">{contentSection}</div>
							</div>
						</td>
					</tr>
				);
			})}
		</>
	);
}

export default ORB3TableRows;
